// ── Following Fyntrac's tenant ──────────────────────────────────────────────
// Insight is opened from Fyntrac with the token and tenant in its URL (kept in sessionStorage). If
// the user then switches tenant in Fyntrac, this tab would go on showing the old one. So whenever
// it comes back into view (and every minute while in view) it asks the Fyntrac window that opened
// it which tenant that window is in now, and reloads in it when it changed. The answer also brings
// a fresh token, which keeps a long-open tab signed in. fyntrac-web answers in its
// StudioTokenBridge, only to Insight's own origin; replies are accepted only from the opener, at
// the origin it gave in the URL.

export const TOKEN_KEY = 'insight_auth_token';
export const TENANT_KEY = 'insight_tenant';
export const OPENER_ORIGIN_KEY = 'insight_opener_origin';

const TENANT_REQUEST = 'fyntrac:tenant-request';
const TENANT_REPLY = 'fyntrac:tenant';
const REPLY_TIMEOUT_MS = 5000;
const SYNC_EVERY_MS = 60 * 1000;

let pending = null;

/** The opener's { tenant, token }, or null (no opener, closed, or no answer within 5 s). */
export function askOpenerTenant() {
  if (pending) return pending;
  const openerOrigin = sessionStorage.getItem(OPENER_ORIGIN_KEY);
  const opener = window.opener;
  if (!opener || opener.closed || !openerOrigin) return Promise.resolve(null);
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  pending = new Promise((resolve) => {
    const finish = (answer) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      pending = null;
      resolve(answer || null);
    };
    const onMessage = (event) => {
      if (event.source !== opener || event.origin !== openerOrigin) return;
      if (event.data?.type !== TENANT_REPLY || event.data.requestId !== requestId) return;
      finish(event.data);
    };
    const timer = setTimeout(() => finish(null), REPLY_TIMEOUT_MS);
    window.addEventListener('message', onMessage);
    try {
      opener.postMessage({ type: TENANT_REQUEST, requestId }, openerOrigin);
    } catch {
      finish(null);
    }
  });
  return pending;
}

/** Applies the opener's answer: a fresh token is kept; another tenant reloads Insight in it. */
export function applyAnswer(answer) {
  if (!answer) return false;
  if (answer.token) sessionStorage.setItem(TOKEN_KEY, answer.token);
  // How Fyntrac's header names the user and tenant: the top bar shows the same after a reload.
  if (answer.tenantName) sessionStorage.setItem('insight_tenantName', answer.tenantName);
  if (answer.userName) sessionStorage.setItem('insight_firstName', answer.userName);
  if (answer.tenant && answer.tenant !== sessionStorage.getItem(TENANT_KEY)) {
    console.info('[Fyntrac Insight] Fyntrac was switched to another tenant: reloading in', answer.tenant);
    sessionStorage.setItem(TENANT_KEY, answer.tenant);
    window.location.reload();
    return true;
  }
  return false;
}

/** Starts following the opener's tenant; returns a function that stops it. */
export function installTenantSync() {
  if (!sessionStorage.getItem(OPENER_ORIGIN_KEY)) return () => {};
  const check = async () => {
    if (document.visibilityState !== 'visible') return;
    applyAnswer(await askOpenerTenant());
  };
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  const timer = setInterval(check, SYNC_EVERY_MS);
  return () => {
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
    clearInterval(timer);
  };
}
